/**
 * Display Devices Workflow Service
 * Provides API methods for workflow and service request management
 */

import { BaseAPIService } from '@/services/baseapiservice';
import type { ApiResponse, WorkflowAction, WorkflowAssignee, WorkflowForwardTargets } from './types';

class DisplayDevicesWorkflowService extends BaseAPIService {
  private basePath = '/display-devices';

  async getServiceRequests(params: Record<string, any> = {}): Promise<ApiResponse<any>> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });
    const qs = query.toString();
    const response = await this.get(`${this.basePath}/service-requests${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getWorkflowInbox(params: Record<string, any> = {}): Promise<ApiResponse<any>> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });
    const qs = query.toString();
    const response = await this.get(`${this.basePath}/workflow/inbox${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  /**
   * Inbox count only — backs the sidebar's "Inbox (N)" badge. The server runs
   * the identical inbox query (same position / designation / ULB / ward
   * scoping) and returns just the number, so the badge can never disagree with
   * the list the officer sees when they open the tab.
   */
  async getWorkflowInboxCount(): Promise<number> {
    const response = await this.get(`${this.basePath}/workflow/inbox/count`);
    const body: any = response.data;
    return Number(body?.data?.count ?? body?.count ?? 0) || 0;
  }

  /**
   * Outbox — applications the logged-in officer has already acted on
   * (forwarded/approved/rejected/returned), with their latest action + the
   * application's current stage. Read-only; a persistent "what I sent on" list.
   */
  async getWorkflowOutbox(params: Record<string, any> = {}): Promise<ApiResponse<any>> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });
    const qs = query.toString();
    const response = await this.get(`${this.basePath}/workflow/outbox${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getWorkflowHistory(instanceId: string): Promise<ApiResponse<any>> {
    const response = await this.get(`${this.basePath}/workflow/${instanceId}`);
    return response.data;
  }

  /**
   * Commissioner accountability report — applications the nightly job auto-
   * escalated because an officer (WPRS / TPO) let the 5-working-day SLA lapse.
   * Returns { breaches, stats }. ULB-scoped server-side.
   */
  async getSlaBreaches(params: Record<string, any> = {}): Promise<ApiResponse<any>> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });
    const qs = query.toString();
    const response = await this.get(`${this.basePath}/workflow/sla-breaches${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getWorkflowByRef(refType: string, refId: number): Promise<ApiResponse<any>> {
    const response = await this.get(`${this.basePath}/workflow/by-ref/${refType}/${refId}`);
    return response.data;
  }

  async performWorkflowAction(instanceId: string, data: WorkflowAction): Promise<ApiResponse<any>> {
    const response = await this.put(`${this.basePath}/workflow/${instanceId}/action`, data);
    return response.data;
  }

  /**
   * Design A (sign-first): stamp the certificate's dates/status and get back
   * { sign_target, rollback } WITHOUT transitioning the workflow, so the Inbox can
   * open the signer while the file stays in the officer's inbox. The caller runs
   * performWorkflowAction(APPROVE) only after the sign completes. Nothing billable
   * or vendor-visible happens here — that all rides on the committed APPROVE.
   */
  async prepareSignForApproval(instanceId: string): Promise<ApiResponse<any>> {
    const response = await this.post(`${this.basePath}/workflow/${instanceId}/prepare-sign`, {});
    return response.data;
  }

  /**
   * LOCAL DEVELOPMENT ONLY — dummy-sign a DDL/DDP document on a dev box where the
   * server's localhost sniff does not fire (app opened on a LAN IP / machine name
   * / tunnel), so the officer would otherwise be stuck on a USB-token PIN popup.
   *
   * Returns the same shape as the normal initiateSignature (so the caller can use
   * it interchangeably), or `null` when the endpoint declines — which is what
   * EVERY deployed server does, because it needs DIGISIGN_LOCAL_DUMMY=true in a
   * developer's gitignored .env.local. Callers must fall back to the real signer
   * on null. Only ever called from an `import.meta.env.DEV` branch, so this never
   * reaches a UAT/production bundle at all.
   */
  async devLocalSign(moduleCode: string, entityId: number): Promise<ApiResponse<any> | null> {
    try {
      const response = await this.post(
        `${this.basePath}/dev/local-sign/${moduleCode}/${entityId}`, {});
      const body: any = response.data;
      // Not an opted-in dev box (404), or the server chose the real signer anyway.
      if (!body?.data?.autoCompleted) return null;
      return body;
    } catch {
      return null; // any failure → silently use the normal signer flow
    }
  }

  /**
   * Undo the stamp above when the officer abandons the signer (closed the modal,
   * signing failed, token unavailable), so the application looks untouched. Pass
   * the `rollback` object prepare-sign returned. Safe to call blindly: the server
   * refuses if the approve actually committed or the document got signed.
   */
  async cancelPrepareSign(instanceId: string, rollback: { prev_sts_cd?: string } | null): Promise<ApiResponse<any>> {
    const response = await this.post(
      `${this.basePath}/workflow/${instanceId}/cancel-prepare-sign`, rollback || {});
    return response.data;
  }

  /**
   * Whether the action modal should offer the department -> designation -> officer
   * picker for this action, and which departments/designations are permitted.
   */
  async getForwardTargets(instanceId: string, actnCd: string): Promise<ApiResponse<WorkflowForwardTargets>> {
    const response = await this.get(
      `${this.basePath}/workflow/${instanceId}/forward-targets?actnCd=${encodeURIComponent(actnCd)}`);
    return response.data;
  }

  /** Serving officers for a designation code, optionally within one department. */
  async getAssignees(designation: string, departmentId?: number | null): Promise<ApiResponse<WorkflowAssignee[]>> {
    const qs = new URLSearchParams({ designation });
    if (departmentId) qs.append('departmentId', String(departmentId));
    const response = await this.get(`${this.basePath}/workflow/assignees?${qs.toString()}`);
    return response.data;
  }

  /** Revenue wards of an application's ULB — for the WPRS "transfer ward" picker. */
  async getInstanceWards(instanceId: string): Promise<ApiResponse<any>> {
    const response = await this.get(`${this.basePath}/workflow/${instanceId}/wards`);
    return response.data;
  }

  /** The logged-in officer's own revenue ward jurisdiction(s) — WPRS "my ward" badge. */
  async getMyWards(): Promise<ApiResponse<any>> {
    const response = await this.get(`${this.basePath}/workflow/my-wards`);
    return response.data;
  }

  /**
   * The first-desk officer transfers a mis-routed application to the locality it
   * actually belongs to. It stays at the first review stage.
   *
   * Two shapes, one endpoint, because the two deployments locate a site
   * differently: a municipal application names a WARD (`targetWardNo`, the
   * revenue ward number), a DTCP one names a VILLAGE SECRETARIAT
   * (`targetSchvlmId`, a ds_schvlm_lst_t key). Send exactly one — they are
   * unrelated id spaces and the server writes a different column for each.
   */
  async transferWard(instanceId: string, data: { targetWardNo?: number | string; targetWardBndryId?: number | string | null; targetSchvlmId?: number | string | null; reason: string }): Promise<ApiResponse<any>> {
    const response = await this.put(`${this.basePath}/workflow/${instanceId}/transfer-ward`, data);
    return response.data;
  }

  // ---------- Reports ----------
  async getOutstandingDemands(params: Record<string, any> = {}): Promise<ApiResponse<any>> {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.append(k, String(v)); });
    const q = qs.toString();
    const response = await this.get(`${this.basePath}/reports/outstanding-demands${q ? `?${q}` : ''}`);
    return response.data;
  }

  async getDisplayersByUlb(): Promise<ApiResponse<any>> {
    const response = await this.get(`${this.basePath}/reports/displayers-by-ulb`);
    return response.data;
  }

  async getInspectionCompliance(): Promise<ApiResponse<any>> {
    const response = await this.get(`${this.basePath}/reports/inspection-compliance`);
    return response.data;
  }

  async getWorkflowSlaStatus(): Promise<ApiResponse<any>> {
    const response = await this.get(`${this.basePath}/reports/workflow-sla`);
    return response.data;
  }

  // ---------- Rates ----------
  async getRateMatrix(ulbId: number): Promise<ApiResponse<any>> {
    const response = await this.get(`${this.basePath}/rates/matrix?ulb_id=${ulbId}`);
    return response.data;
  }

  async getEffectiveRate(params: Record<string, any>): Promise<ApiResponse<any>> {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.append(k, String(v)); });
    const response = await this.get(`${this.basePath}/rates/effective?${qs.toString()}`);
    return response.data;
  }

  async listRates(params: Record<string, any> = {}): Promise<ApiResponse<any>> {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.append(k, String(v)); });
    const q = qs.toString();
    const response = await this.get(`${this.basePath}/rates${q ? `?${q}` : ''}`);
    return response.data;
  }

  async saveRate(data: any): Promise<ApiResponse<any>> {
    const response = await this.post(`${this.basePath}/rates`, data);
    return response.data;
  }

  async deleteRate(id: number): Promise<ApiResponse<any>> {
    const response = await this.delete(`${this.basePath}/rates/${id}`);
    return response.data;
  }

  async getRateHistory(id: number): Promise<ApiResponse<any>> {
    const response = await this.get(`${this.basePath}/rates/${id}/history`);
    return response.data;
  }
}

export const displayDevicesWorkflowService = new DisplayDevicesWorkflowService();
export default displayDevicesWorkflowService;
